import { usePortalValue, usePortalMessage, type PortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="Numbers and SIP"` — unit P11-05, laid out as the design draws it (ADR 0018).
 *
 * AMENDED 2026-10-10 (numbers-sip-sync, the Claude Design export of 2026-10-10, owner: "the prototype wins"). The
 * page is the prototype's again:
 *
 *   · the header — "Numbers & SIP", "Connect a trunk" (Owners and Admins, `sip_trunk.manage`) and "Request a number";
 *   · a view switcher, "Your numbers · N" / "Released · N", with "Where to point your provider" beside it, which
 *     opens the dialog that replaced the edge card (`./point-provider-dialog.tsx`);
 *   · one row per number, reading "agent · connection · provider" with its status, opening onto Details (Agent,
 *     Connected through, Since, Last call, This month), the test action ("Test call", or "Call me" for a number that
 *     only makes calls), "Change agent", "Release number", and Recent activity with "See all calls on this number";
 *   · "Released" — the numbers this workspace no longer has, with who used them, the provider, when, and why.
 *
 * WHERE THE DATA COMES FROM. Details' "Last call" and "This month" and the Recent activity are
 * `GET /v1/numbers/{id}/activity` — the conversations through the number's bindings while the workspace held it,
 * across a rebind — and "See all calls on this number" is History filtered by the same predicate
 * (`phone_number_id`). "Why it was released" is how the number left (`release_cause`): nobody records a typed
 * reason, so none is shown. A pending number request is a "Setting up" row: WeTalk has not connected a number yet,
 * so it has no number to test, move or release, and says so.
 *
 * WHAT THE PROTOTYPE DROPPED, AND WHERE IT STILL IS. The export took the trunk card off this page — its facts, the
 * password's Replace, Check connection, Test call, Edit, Disable, Delete and Recent signalling — and drew them
 * nowhere else. They are real capabilities (connecting numbers to a trunk is Edit; a disabled trunk is how a member
 * stops it), so they stay reachable in the least intrusive place: "Your SIP trunks", below the list, one collapsed
 * row per trunk that opens onto the unchanged card and its SIP activity (`./sip-trunk-card.tsx`) — the inbound
 * sign-in details shown once and the inbound-only rendering (plan-015 SIP) included.
 *
 * WHAT EACH NUMBER'S ACTIONS CAN DO. A number on your own trunk is yours to test, move between agents and release
 * (the trunk routes, Owners and Admins). A number WeTalk issued is connected and released by WeTalk — no customer
 * route changes it — so its "Change agent" and "Release number" are drawn disabled with that reason; testing it is
 * ringing it, and its "Test call" says so.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Dialog,
  Field,
  Icon,
  Segmented,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import "./numbers.css";

import { displayNumberOf } from "./number-display.js";
import { NumberRequestDialog, countryName, numberRequestListQuery } from "./number-request.js";
import {
  NUMBER_STATUS_TONE,
  NUMBER_STATUS_WORD,
  RELEASE_CAUSE_WORD,
  activityLineOf,
  connectedThroughOf,
  connectionWordOf,
  monthWordOf,
  numberRoleOf,
  numberStatusOf,
  providerWordOf,
  voiceAgentWordOf,
  type ActivityMark,
} from "./number-row-copy.js";
import { PointProviderDialog } from "./point-provider-dialog.js";
import { SipTrunkCards } from "./sip-trunk-card.js";
import { INBOUND_ONLY_TRUNK_STATE_WORD, TRUNK_STATE_WORD } from "./sip-trunk-copy.js";
import { SipTrunkDialog, type TrunkSaved } from "./sip-trunk-dialog.js";
import { SIP_TRUNK_QUERY_KEY, sipTrunkListQuery, sipTrunkSettingsQuery } from "./sip-trunk-query.js";
import { SipTrunkTestCallDialog } from "./sip-trunk-test-call.js";
import { GLYPH } from "../../shell/glyph.js";
import { useMemberCan } from "../../shell/member-capability.js";
import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import {
  QUERY_KEY,
  numberActivityQuery,
  numberAvailabilityKeyedQuery,
  numberQuery,
  voiceAgentListQuery,
} from "../../api/query.js";
import type { NumberRowView } from "../../api/gateway.js";
import type { NumberRequestView } from "../../api/slice/pending-request.js";
import { isInboundOnlyTrunkView, type SipTrunkView } from "../../api/slice/sip-trunk.js";

type TrunkDialog = { readonly kind: "create" } | { readonly kind: "edit"; readonly trunk: SipTrunkView } | null;

type NumberView = "active" | "released";

/** What the page says under a trunk after a save. */
function noticeOf(saved: TrunkSaved): string {
  if (saved.mode === "create") {
    return portalText("operations", "value_is_connected_and_not_verified_yet_check_the_connecti_23b5a849", { v0: String(saved.displayName) });
  }
  return saved.reverify
    ? portalText("operations", "saved_the_connection_details_changed_so_check_the_connecti_3fbc0857")
    : portalText("operations", "saved_9d4af690");
}

/** One row of the list: a number the workspace holds, or a number request WeTalk has not fulfilled yet. */
type ListedRow =
  | { readonly kind: "number"; readonly key: string; readonly row: NumberRowView }
  | { readonly kind: "request"; readonly key: string; readonly request: NumberRequestView };

export function NumbersScreen(): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const numbers = useQuery(numberQuery(gateway));
  const trunks = useQuery(sipTrunkListQuery(gateway));
  const settings = useQuery(sipTrunkSettingsQuery(gateway));
  const availability = useQuery(numberAvailabilityKeyedQuery(gateway));
  const requests = useQuery(numberRequestListQuery(gateway));
  const [view, setView] = useState<NumberView>("active");
  /** `undefined` until the member opens or closes one: the first row starts open, as the prototype draws it. */
  const [openKey, setOpenKey] = useState<string | null | undefined>(undefined);
  const [openTrunkId, setOpenTrunkId] = usePortalMessage();
  const [pointOpen, setPointOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [trunkDialog, setTrunkDialog] = useState<TrunkDialog>(null);
  const [notice, setNotice] = usePortalValue<{ readonly displayName: string; readonly text: string } | null>(null);
  // plan-008 (ST-90): connecting a trunk is Owner or Admin (the trunk routes' own role list) — offered only
  // to them; a Member or a Viewer is told who can instead.
  const member = useMemberCan();
  const canManageTrunk = member.can("sip_trunk.manage");

  if (numbers.data === undefined) {
    return (
      <section className="c-page nb-page">
        <p className="c-note">{portalText("operations", "reading_your_numbers_7f156746")}</p>
      </section>
    );
  }

  const held = numbers.data.row.filter((one) => one.releasedAtText === null);
  const released = numbers.data.row.filter((one) => one.releasedAtText !== null);
  const pending = (requests.data ?? []).filter((one) => one.state !== "connected");
  const listed: ListedRow[] = [
    ...held.map((row): ListedRow => ({ kind: "number", key: `number:${row.phoneNumberId}`, row })),
    ...pending.map((request): ListedRow => ({ kind: "request", key: `request:${request.numberRequestId}`, request })),
  ];
  const shownOpenKey = openKey === undefined ? (listed[0]?.key ?? null) : openKey;
  const trunkOf = (row: NumberRowView): SipTrunkView | undefined =>
    row.sipTrunkId === null ? undefined : trunks.data?.find((one) => one.sipTrunkId === row.sipTrunkId);

  return (
    <section className="c-page nb-page">
      <div className="nb-head">
        <div>
          <h1 className="nb-title">{portalText("operations", "numbers_sip_dad16cb4")}</h1>
        </div>
        <div className="nb-actions">
          {canManageTrunk ? (
            <Button
              variant="secondary"
              onClick={() => {
                setTrunkDialog({ kind: "create" });
              }}
            >
              <Icon glyph={GLYPH.plug} size="md" />
              {portalText("operations", "connect_a_trunk_bed9b758")}</Button>
          ) : null}
          <Button
            variant="primary"
            onClick={() => {
              setRequestOpen(true);
            }}
          >
            <Icon glyph={GLYPH.plus} size="md" />
            {portalText("operations", "request_a_number_705e332d")}</Button>
        </div>
      </div>

      {availability.data === undefined || availability.data.available ? null : (
        <p className="nb-note">{availability.data.detail}</p>
      )}

      <NumberRequestDialog
        open={requestOpen}
        onClose={() => {
          setRequestOpen(false);
        }}
      />

      <div className="nb-toolbar">
        <Segmented
          name="numView"
          legend={portalText("finance", "merge.numbers.whichNumbers")}
          value={view}
          options={[
            { value: "active", label: portalText("finance", "merge.numbers.yourNumbers", { count: listed.length }) },
            { value: "released", label: portalText("finance", "merge.numbers.releasedCount", { count: released.length }) },
          ]}
          onChange={setView}
        />
        <span className="nb-toolbar-end">
          <Button
            variant="ghost"
            onClick={() => {
              setPointOpen(true);
            }}
          >
            <Icon glyph={GLYPH.info} size="md" />
            <span>{portalText("finance", "merge.numbers.pointProvider")}</span>
          </Button>
        </span>
      </div>

      {view === "active" ? (
        listed.length === 0 ? (
          <div className="nb-card nb-empty">
            <p className="nb-card-title">{portalText("finance", "merge.numbers.noNumbers")}</p>
            <p className="nb-note">{portalText("finance", "merge.numbers.empty")}</p>
          </div>
        ) : (
          <div className="nb-rows">
            {listed.map((one) => {
              const open = shownOpenKey === one.key;
              const toggle = (): void => {
                setOpenKey(open ? null : one.key);
              };
              return one.kind === "number" ? (
                <NumberRowCard
                  key={one.key}
                  row={one.row}
                  trunk={trunkOf(one.row)}
                  open={open}
                  onToggle={toggle}
                  canManageTrunk={canManageTrunk}
                  whyNotTrunk={member.whyNot("sip_trunk.manage")}
                />
              ) : (
                <RequestRowCard key={one.key} request={one.request} open={open} onToggle={toggle} />
              );
            })}
          </div>
        )
      ) : (
        <ReleasedNumbers released={released} />
      )}

      {view === "active" ? (
        <SipTrunkSection
          trunks={trunks.data}
          trunksError={trunks.isError ? apiErrorText(trunks.error, portalText("finance", "merge.numbers.cantReadTrunks")) : null}
          settings={settings.data}
          openTrunkId={openTrunkId}
          onToggle={(sipTrunkId) => {
            setOpenTrunkId(openTrunkId === sipTrunkId ? null : sipTrunkId);
          }}
          notice={notice}
          onEdit={(trunk) => {
            setTrunkDialog({ kind: "edit", trunk });
          }}
        />
      ) : null}

      {pointOpen ? (
        <PointProviderDialog
          settings={settings.data}
          onClose={() => {
            setPointOpen(false);
          }}
        />
      ) : null}

      {trunkDialog === null || !canManageTrunk ? null : (
        <SipTrunkDialog
          trunk={trunkDialog.kind === "edit" ? trunkDialog.trunk : null}
          settings={settings.data}
          onClose={() => {
            setTrunkDialog(null);
          }}
          onSaved={(saved) => {
            setNotice(() => ({ displayName: saved.displayName, text: noticeOf(saved) }));
            // The saved trunk opens, so its notice and its next step are where the member looks.
            const savedTrunk = trunks.data?.find((one) => one.displayName === saved.displayName);
            setOpenTrunkId(savedTrunk?.sipTrunkId ?? `name:${saved.displayName}`);
          }}
        />
      )}
    </section>
  );
}

// --- one number ---------------------------------------------------------------------------

type NumberDialog = "test_call" | "carrier_test" | "change_voice_agent" | "release" | null;

function NumberRowCard({
  row,
  trunk,
  open,
  onToggle,
  canManageTrunk,
  whyNotTrunk,
}: {
  readonly row: NumberRowView;
  readonly trunk: SipTrunkView | undefined;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly canManageTrunk: boolean;
  readonly whyNotTrunk: string | null;
}): ReactNode {
  usePortalTranslation("finance");
  const trunkDials = trunk !== undefined && !isInboundOnlyTrunkView(trunk);
  const role = numberRoleOf(row, trunkDials);
  const status = numberStatusOf(row);
  const voiceAgent = voiceAgentWordOf(row);
  return (
    <div className="nb-row">
      <button type="button" className="nb-row-head" aria-expanded={open} onClick={onToggle}>
        <span className="nb-row-main">
          <span className="nb-row-number">{displayNumberOf(row.e164)}</span>
          <span className="nb-row-line">
            {voiceAgent} · {connectionWordOf(role, role.dials && trunk !== undefined ? trunk.allowedDestinationCountry : [])} ·{" "}
            {providerWordOf(row)}
          </span>
        </span>
        <Tag tone={NUMBER_STATUS_TONE[status]}>{NUMBER_STATUS_WORD[status]}</Tag>
        <Icon glyph={open ? GLYPH.caretUp : GLYPH.caretDown} size="md" />
      </button>
      {open ? (
        <NumberDetail
          row={row}
          trunk={trunk}
          role={role}
          voiceAgent={voiceAgent}
          canManageTrunk={canManageTrunk}
          whyNotTrunk={whyNotTrunk}
        />
      ) : null}
    </div>
  );
}

function NumberDetail({
  row,
  trunk,
  role,
  voiceAgent,
  canManageTrunk,
  whyNotTrunk,
}: {
  readonly row: NumberRowView;
  readonly trunk: SipTrunkView | undefined;
  readonly role: { readonly answers: boolean; readonly dials: boolean };
  readonly voiceAgent: string;
  readonly canManageTrunk: boolean;
  readonly whyNotTrunk: string | null;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const activity = useQuery(numberActivityQuery(gateway, row.phoneNumberId));
  const [dialog, setDialog] = useState<NumberDialog>(null);
  const [status, setStatus] = usePortalMessage();
  const onTrunk = row.sipTrunkId !== null;
  // portalText("finance", "merge.numbers.callMe") for a number that only makes calls (the prototype's `testLabel`), portalText("finance", "merge.numbers.testCall") otherwise.
  const ringsMe = role.dials && !role.answers;
  const testCallLabel = ringsMe ? portalText("finance", "merge.numbers.callMe") : portalText("finance", "merge.numbers.testCall");

  const lastConversation =
    activity.data === undefined ? (activity.isError ? portalText("finance", "merge.numbers.notAvailable") : "…") : (activity.data.lastConversationAtText ?? "—");
  const thisMonth =
    activity.data === undefined ? (activity.isError ? portalText("finance", "merge.numbers.notAvailable") : "…") : monthWordOf(activity.data.monthConversationCount);

  // Why a test cannot run yet, in the member's words; `null` when it can.
  const testCallBlocked: string | null = onTrunk
    ? trunk === undefined
      ? portalText("finance", "merge.numbers.readingTrunk")
      : trunk.state === "disabled"
        ? portalText("finance", "merge.numbers.trunkOff", { name: trunk.displayName })
        : !role.answers && !role.dials
          ? portalText("finance", "merge.numbers.connectFirst")
          : null
    : row.channelBindingId === null
      ? portalText("finance", "merge.numbers.connectFirst")
      : null;

  return (
    <div className="nb-row-body">
      <div>
        <p className="nb-row-section">{portalText("finance", "merge.numbers.details")}</p>
        <dl className="nb-row-facts">
          <dt>{portalText("finance", "merge.numbers.agent")}</dt>
          <dd>{voiceAgent}</dd>
          <dt>{portalText("finance", "merge.numbers.connectedThrough")}</dt>
          <dd>{connectedThroughOf(row)}</dd>
          <dt>{portalText("finance", "merge.numbers.since")}</dt>
          <dd>{row.purchasedOn}</dd>
          <dt>{portalText("finance", "merge.numbers.lastCall")}</dt>
          <dd>{lastConversation}</dd>
          <dt>{portalText("finance", "merge.numbers.month")}</dt>
          <dd>{thisMonth}</dd>
        </dl>
        {onTrunk && !canManageTrunk ? (
          whyNotTrunk === null ? null : <p className="nb-note">{whyNotTrunk}</p>
        ) : (
          <>
            <div className="nb-row-actions">
              <Button
                variant="secondary"
                disabled={testCallBlocked !== null}
                onClick={() => {
                  setDialog(onTrunk ? "test_call" : "carrier_test");
                }}
              >
                {testCallLabel}
              </Button>
              <Button
                variant="secondary"
                disabled={!onTrunk}
                onClick={() => {
                  setDialog("change_voice_agent");
                }}
              >{portalText("finance", "merge.numbers.changeAgent")}</Button>
              <span className="nb-danger-wrap">
                <Button
                  variant="ghost"
                  disabled={!onTrunk}
                  onClick={() => {
                    setDialog("release");
                  }}
                >{portalText("finance", "merge.numbers.releaseNumber")}</Button>
              </span>
            </div>
            {testCallBlocked === null || testCallBlocked === portalText("finance", "merge.numbers.readingTrunk") ? null : <p className="nb-note">{testCallBlocked}</p>}
            {onTrunk ? null : (
              <p className="nb-note">{portalText("finance", "merge.numbers.platformMove")}</p>
            )}
          </>
        )}
        {status === null ? null : (
          <p className="nb-note" role="status">
            {status}
          </p>
        )}
      </div>

      <div>
        <p className="nb-row-section">{portalText("finance", "merge.numbers.recentActivity")}</p>
        {activity.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {apiErrorText(activity.error, portalText("finance", "merge.numbers.activityReadError"))}
          </p>
        ) : activity.data === undefined ? (
          <p className="nb-note">{portalText("finance", "merge.numbers.readingActivity")}</p>
        ) : activity.data.conversation.length === 0 ? (
          <p className="nb-note">{portalText("finance", "merge.numbers.noCalls")}</p>
        ) : (
          <ul className="nb-activity">
            {activity.data.conversation.map((one) => {
              const line = activityLineOf(one);
              return (
                <li key={one.conversationId} className="nb-activity-row">
                  <span className={`nb-activity-mark nb-activity-mark-${line.mark}`}>
                    <Icon glyph={MARK_GLYPH[line.mark]} size="md" />
                  </span>
                  <span className="nb-activity-text">
                    <span className="nb-activity-title">{line.title}</span>
                    <span className="nb-activity-detail">{line.detail}</span>
                  </span>
                  <span className="nb-activity-when">{one.startedAtText}</span>
                </li>
              );
            })}
          </ul>
        )}
        {row.voiceAgentId === null || row.channelBindingId === null ? null : (
          <Link
            to="/agents/$voiceAgentId"
            params={{ voiceAgentId: row.voiceAgentId }}
            search={{ tab: "history", range: "all", outcome: "all", q: "", conversation: "", number: row.phoneNumberId }}
            className="nb-all-calls"
          >{portalText("finance", "merge.numbers.allCalls")}</Link>
        )}
      </div>

      {dialog === "test_call" && trunk !== undefined ? (
        <SipTrunkTestCallDialog
          trunk={trunk}
          phoneNumberId={row.phoneNumberId}
          {...(ringsMe ? { direction: "agent_calls_me" as const } : {})}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === "carrier_test" ? (
        <Dialog
          open
          title={portalText("finance", "merge.numbers.testNumber", { number: displayNumberOf(row.e164) })}
          onClose={() => {
            setDialog(null);
          }}
          actions={
            <Button
              variant="primary"
              onClick={() => {
                setDialog(null);
              }}
            >{portalText("finance", "merge.numbers.done")}</Button>
          }
        >
          <p className="c-note">
            {portalText("finance", "merge.numbers.ringNumber", { number: displayNumberOf(row.e164), name: voiceAgent })}
          </p>
        </Dialog>
      ) : null}
      {dialog === "change_voice_agent" && row.sipTrunkId !== null ? (
        <ChangeVoiceAgentDialog
          row={row}
          sipTrunkId={row.sipTrunkId}
          onClose={() => {
            setDialog(null);
          }}
          onChanged={(text) => {
            setStatus(text);
          }}
        />
      ) : null}
      {dialog === "release" && row.sipTrunkId !== null ? (
        <ReleaseNumberDialog
          row={row}
          sipTrunkId={row.sipTrunkId}
          trunkName={row.sipTrunkDisplayName}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
    </div>
  );
}

const MARK_GLYPH: Readonly<Record<ActivityMark, (typeof GLYPH)[keyof typeof GLYPH]>> = {
  in: GLYPH.phoneIncoming,
  out: GLYPH.phoneOutgoing,
  miss: GLYPH.phoneX,
};

/** "Change agent" for a number on your own trunk: the trunk's bind and unbind (plan-008). */
function ChangeVoiceAgentDialog({
  row,
  sipTrunkId,
  onClose,
  onChanged,
}: {
  readonly row: NumberRowView;
  readonly sipTrunkId: string;
  readonly onClose: () => void;
  readonly onChanged: (text: PortalMessage) => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const voiceAgentList = useQuery(voiceAgentListQuery(gateway));
  const [chosen, setChosen] = useState(row.channelBindingId === null ? "" : (row.voiceAgentId ?? ""));
  const [problem, setProblem] = usePortalMessage();
  const voiceAgents = voiceAgentList.data ?? [];
  const known = voiceAgents.some((one) => one.voiceAgentId === row.voiceAgentId);
  const change = useMutation({
    mutationFn: () =>
      chosen === ""
        ? gateway.unbindSipTrunkNumber(sipTrunkId, row.phoneNumberId)
        : gateway.bindSipTrunkNumber(sipTrunkId, row.phoneNumberId, chosen).then(() => undefined),
    onSuccess: () => {
      const name = voiceAgents.find((one) => one.voiceAgentId === chosen)?.displayName ?? null;
      onChanged(() =>
        chosen === ""
          ? portalText("finance", "merge.numbers.disconnected", { number: displayNumberOf(row.e164) })
          : portalText("finance", "merge.numbers.reconnected", { number: displayNumberOf(row.e164), name: name ?? portalText("finance", "merge.numbers.chosenAgent") }),
      );
      onClose();
    },
    onError: (raised: unknown) => {
      setProblem(() => apiErrorText(raised, portalText("finance", "merge.numbers.connectError")));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
        queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
      ]);
    },
  });
  return (
    <Dialog
      open
      title={portalText("finance", "merge.numbers.changeNumberAgent", { number: displayNumberOf(row.e164) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("finance", "merge.numbers.cancel")}</Button>
          <Button
            variant="primary"
            disabled={change.isPending || chosen === (row.channelBindingId === null ? "" : (row.voiceAgentId ?? ""))}
            onClick={() => {
              setProblem(null);
              change.mutate();
            }}
          >
            {change.isPending ? portalText("finance", "merge.numbers.saving") : portalText("finance", "merge.numbers.save")}
          </Button>
        </>
      }
    >
      <div className="nb-form">
        <Field label={portalText("finance", "merge.numbers.answeringAgent")} description={portalText("finance", "merge.numbers.currentCalls")}>
          {(control) => (
            <Select
              {...control}
              value={chosen}
              onChange={(event) => {
                setChosen(event.currentTarget.value);
              }}
            >
              <option value="">{portalText("finance", "merge.numbers.notConnected")}</option>
              {row.voiceAgentId !== null && row.channelBindingId !== null && !known ? (
                <option value={row.voiceAgentId}>{row.voiceAgentName ?? portalText("finance", "merge.numbers.anAgent")}</option>
              ) : null}
              {voiceAgents.map((one) => (
                <option key={one.voiceAgentId} value={one.voiceAgentId}>
                  {one.displayName}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {problem === null ? null : (
          <p className="c-note c-note-critical" role="alert">
            {problem}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/** "Release number" for a number on your own trunk: removed from the trunk, kept as history (plan-008). */
function ReleaseNumberDialog({
  row,
  sipTrunkId,
  trunkName,
  onClose,
}: {
  readonly row: NumberRowView;
  readonly sipTrunkId: string;
  readonly trunkName: string | null;
  readonly onClose: () => void;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [problem, setProblem] = usePortalMessage();
  const release = useMutation({
    mutationFn: () => gateway.removeSipTrunkNumber(sipTrunkId, row.phoneNumberId),
    onSuccess: () => {
      onClose();
    },
    onError: (raised: unknown) => {
      setProblem(() => apiErrorText(raised, portalText("finance", "merge.numbers.releaseError")));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.number }),
        queryClient.invalidateQueries({ queryKey: SIP_TRUNK_QUERY_KEY.sipTrunk }),
      ]);
    },
  });
  return (
    <Dialog
      open
      title={portalText("finance", "merge.numbers.releaseQuestion", { number: displayNumberOf(row.e164) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>{portalText("finance", "merge.numbers.keep")}</Button>
          <Button
            variant="primary"
            disabled={release.isPending}
            onClick={() => {
              setProblem(null);
              release.mutate();
            }}
          >
            {release.isPending ? portalText("finance", "merge.numbers.releasing") : portalText("finance", "merge.numbers.releaseAction")}
          </Button>
        </>
      }
    >
      <p className="c-note">
        {portalText("finance", "merge.numbers.releaseExplanation", { trunk: trunkName ?? portalText("finance", "merge.numbers.yourTrunk") })}
      </p>
      {problem === null ? null : (
        <p className="c-note c-note-critical" role="alert">
          {problem}
        </p>
      )}
    </Dialog>
  );
}

// --- a number request WeTalk has not fulfilled yet -----------------------------------------

function RequestRowCard({
  request,
  open,
  onToggle,
}: {
  readonly request: NumberRequestView;
  readonly open: boolean;
  readonly onToggle: () => void;
}): ReactNode {
  usePortalTranslation("finance");
  const country = countryName(request.country);
  return (
    <div className="nb-row">
      <button type="button" className="nb-row-head" aria-expanded={open} onClick={onToggle}>
        <span className="nb-row-main">
          <span className="nb-row-number">{portalText("finance", "merge.numbers.newNumber", { country })}</span>
          <span className="nb-row-line">{portalText("finance", "merge.numbers.requestedLine", { name: request.voiceAgentName })}</span>
        </span>
        <Tag tone={NUMBER_STATUS_TONE.setting_up}>{NUMBER_STATUS_WORD.setting_up}</Tag>
        <Icon glyph={open ? GLYPH.caretUp : GLYPH.caretDown} size="md" />
      </button>
      {open ? (
        <div className="nb-row-body">
          <div>
            <p className="nb-row-section">{portalText("finance", "merge.numbers.details")}</p>
            <dl className="nb-row-facts">
              <dt>{portalText("finance", "merge.numbers.agent")}</dt>
              <dd>{request.voiceAgentName}</dd>
              <dt>{portalText("finance", "merge.numbers.connectedThrough")}</dt>
              <dd>{portalText("finance", "merge.numbers.requested")}</dd>
              <dt>{portalText("finance", "merge.numbers.since")}</dt>
              <dd>{portalText("finance", "merge.numbers.requestedAt", { at: request.requestedAtText })}</dd>
              <dt>{portalText("finance", "merge.numbers.lastCall")}</dt>
              <dd>—</dd>
              <dt>{portalText("finance", "merge.numbers.month")}</dt>
              <dd>{portalText("finance", "merge.numbers.settingUpNumber")}</dd>
            </dl>
            <div className="nb-row-actions">
              <Button variant="secondary" disabled>{portalText("finance", "merge.numbers.testCall")}</Button>
              <Button variant="secondary" disabled>{portalText("finance", "merge.numbers.changeAgent")}</Button>
              <span className="nb-danger-wrap">
                <Button variant="ghost" disabled>{portalText("finance", "merge.numbers.releaseNumber")}</Button>
              </span>
            </div>
            <p className="nb-note">{portalText("finance", "merge.numbers.readyActions")}</p>
          </div>
          <div>
            <p className="nb-row-section">{portalText("finance", "merge.numbers.recentActivity")}</p>
            <ul className="nb-activity">
              <li className="nb-activity-row">
                <span className="nb-activity-mark nb-activity-mark-info">
                  <Icon glyph={GLYPH.info} size="md" />
                </span>
                <span className="nb-activity-text">
                  <span className="nb-activity-title">{portalText("finance", "merge.numbers.requested")}</span>
                  <span className="nb-activity-detail">
                    {country}
                    {request.note === "" ? "" : portalText("finance", "merge.numbers.note", { note: request.note })}
                  </span>
                </span>
                <span className="nb-activity-when">{request.requestedAtText}</span>
              </li>
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// --- the numbers you no longer have --------------------------------------------------------

function ReleasedNumbers({ released }: { readonly released: readonly NumberRowView[] }): ReactNode {
  usePortalTranslation("finance");
  return (
    <>
      <p className="nb-released-note">{portalText("finance", "merge.numbers.releasedNote")}</p>
      {released.length === 0 ? (
        <div className="nb-card">
          <p className="nb-note nb-note-flush">{portalText("finance", "merge.numbers.noneReleased")}</p>
        </div>
      ) : (
        <div className="nb-released nb-table">
          <Table caption={portalText("finance", "merge.numbers.releasedCaption")} captionVisible={false}>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{portalText("finance", "merge.numbers.number")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "merge.numbers.usedBy")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "merge.numbers.connectedThrough")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "merge.numbers.inUse")}</TableHeaderCell>
                <TableHeaderCell>{portalText("finance", "merge.numbers.releaseWhy")}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {released.map((row) => (
                <TableRow key={row.phoneNumberId}>
                  <TableCell>{displayNumberOf(row.e164)}</TableCell>
                  <TableCell>{row.voiceAgentName ?? portalText("finance", "merge.numbers.noAgent")}</TableCell>
                  <TableCell>{providerWordOf(row)}</TableCell>
                  <TableCell>
                    {row.purchasedOn} – {row.releasedOn ?? "—"}
                  </TableCell>
                  <TableCell>{row.releaseCause === null ? portalText("finance", "merge.numbers.released") : RELEASE_CAUSE_WORD[row.releaseCause]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}

// --- your SIP trunks: what the prototype took off this page, kept reachable -----------------

function SipTrunkSection({
  trunks,
  trunksError,
  settings,
  openTrunkId,
  onToggle,
  notice,
  onEdit,
}: {
  readonly trunks: readonly SipTrunkView[] | undefined;
  readonly trunksError: string | null;
  readonly settings: Parameters<typeof SipTrunkCards>[0]["settings"];
  readonly openTrunkId: string | null;
  readonly onToggle: (sipTrunkId: string) => void;
  readonly notice: { readonly displayName: string; readonly text: string } | null;
  readonly onEdit: (trunk: SipTrunkView) => void;
}): ReactNode {
  usePortalTranslation("finance");
  if (trunksError !== null) {
    return (
      <p className="c-note c-note-critical nb-trunks" role="alert">
        {trunksError}
      </p>
    );
  }
  if (trunks === undefined || trunks.length === 0) return null;
  return (
    <div className="nb-trunks">
      <h2 className="nb-trunks-title">{portalText("finance", "merge.numbers.yourTrunks")}</h2>
      <div className="nb-rows">
        {trunks.map((trunk) => {
          const open = openTrunkId === trunk.sipTrunkId || openTrunkId === `name:${trunk.displayName}`;
          const inboundOnly = isInboundOnlyTrunkView(trunk);
          const stateWord = (inboundOnly ? INBOUND_ONLY_TRUNK_STATE_WORD : TRUNK_STATE_WORD)[trunk.state];
          const count = trunk.number.length;
          return (
            <div key={trunk.sipTrunkId} className="nb-row">
              <button
                type="button"
                className="nb-row-head"
                aria-expanded={open}
                onClick={() => {
                  onToggle(trunk.sipTrunkId);
                }}
              >
                <span className="nb-row-main">
                  <span className="nb-row-number">{trunk.displayName}</span>
                  <span className="nb-row-line">
                    {portalText("finance", "merge.numbers.trunkNumbers", { count })} · {inboundOnly ? portalText("finance", "merge.numbers.receivesOnly") : portalText("finance", "merge.numbers.answersMakes")} · {portalText("finance", "merge.numbers.settingsActivity")}
                  </span>
                </span>
                <Tag tone={trunk.state === "verified" ? "accent" : "neutral"}>{stateWord}</Tag>
                <Icon glyph={open ? GLYPH.caretUp : GLYPH.caretDown} size="md" />
              </button>
              {open ? (
                <div className="nb-trunk-body">
                  <SipTrunkCards
                    trunk={trunk}
                    settings={settings}
                    notice={notice !== null && notice.displayName === trunk.displayName ? notice.text : null}
                    onEdit={onEdit}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
